import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/torq/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POSTER =
  "https://customer-assets-jai6qajn.emergentagent.net/job_bc26bf95-8fb5-48d5-af5e-27cdfa8eed60/artifacts/dab1367d000a9747_TC%201%20insta%20post.webp";

export default function Notify({ onReserve }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [division, setDivision] = useState("MENS_OPEN");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const posterY = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);

  useEffect(() => {
    fetch(`${API}/notify/count`)
      .then((r) => r.json())
      .then((d) => setCount(d.count || 0))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, division }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Something went wrong");
      setCount(data.count || count + 1);
      setDone(true);
      toast.success(data.already ? "You're already on the list — still got it." : "You're on the list. Watch your inbox.");
    } catch (err) {
      toast.error(err.message || "Could not reach the list. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="notify"
      ref={ref}
      className="border-b border-[#2e2e2a] px-6 lg:px-12 py-24 lg:py-32"
      data-testid="notify-section"
    >
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal>
          <div className="relative max-w-md mx-auto lg:mx-0" data-testid="notify-poster-frame">
            <div className="relative overflow-hidden border border-[#2e2e2a] aspect-square">
              <motion.img
                src={POSTER}
                alt="Official TORQ Circuit poster — Delhi, India, coming soon"
                style={{ y: posterY }}
                className="absolute inset-0 w-full h-[108%] object-cover"
                data-testid="notify-poster"
              />
            </div>
            <p
              className="mt-4 font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a]"
              data-testid="notify-poster-caption"
            >
              OFFICIAL POSTER — SEASON ONE
            </p>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.35em] text-[#ff5a1f] mb-8" data-testid="notify-overline">
              04 / DELHI EDITION
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              className="font-display font-extrabold uppercase leading-[0.9] text-[clamp(2.8rem,6vw,6rem)]"
              data-testid="notify-heading"
            >
              DELHI. <span className="text-stroke">THE FIRST CIRCUIT.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 max-w-xl text-[#a3a39f] leading-relaxed text-base lg:text-lg" data-testid="notify-copy">
              Venue, date and tickets are locked in the war room and drop soon. Join the list for
              first access when registration opens — the list gets in before anyone else.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <p
              className="mt-6 font-mono text-[11px] tracking-[0.25em] text-[#ff5a1f] border border-[#ff5a1f]/40 inline-block px-4 py-2"
              data-testid="notify-count"
            >
              {`${Math.max(count, 100)} ATHLETES ALREADY ON THE LIST`}
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <button
              onClick={() => onReserve(division)}
              data-testid="reserve-open-btn"
              className="btn-solid mt-8 w-full max-w-xl border border-[#ff5a1f] text-[#ff5a1f] font-mono text-xs tracking-[0.25em] py-4 hover:bg-[#ff5a1f] hover:text-[#0c0c0a] transition-colors"
            >
              RESERVE YOUR SPOT — DEPOSIT
            </button>
            <div className="mt-6 mb-1 flex items-center gap-4 max-w-xl">
              <span className="flex-1 h-px bg-[#2e2e2a]" />
              <span className="font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a]">
                OR JOIN THE FREE LIST
              </span>
              <span className="flex-1 h-px bg-[#2e2e2a]" />
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            {done ? (
              <div
                className="mt-10 border border-[#454540] bg-[#1e1e1c] p-8 flex items-start gap-4"
                data-testid="notify-success"
              >
                <span className="mt-0.5 border border-[#f0eee4] p-2">
                  <Check size={16} />
                </span>
                <div>
                  <p className="font-display font-extrabold uppercase text-2xl" data-testid="notify-success-title">
                    YOU'RE IN{name ? `, ${name.split(" ")[0].toUpperCase()}` : ""}.
                  </p>
                  <p className="mt-2 text-sm text-[#a3a39f]" data-testid="notify-success-copy">
                    We'll ping your inbox the moment Delhi goes live. Until then — train everything.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-10 max-w-xl" data-testid="notify-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="notify-name" className="font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] block mb-2">
                      NAME
                    </label>
                    <input
                      id="notify-name"
                      className="field"
                      placeholder="YOUR NAME"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      maxLength={80}
                      data-testid="notify-form-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="notify-division" className="font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] block mb-2">
                      DIVISION INTEREST
                    </label>
                    <select
                      id="notify-division"
                      className="field"
                      value={division}
                      onChange={(e) => setDivision(e.target.value)}
                      data-testid="notify-form-division"
                    >
                      <option value="MENS_OPEN">MEN'S OPEN</option>
                      <option value="WOMENS_OPEN">WOMEN'S OPEN</option>
                      <option value="MENS_DOUBLES">MEN'S DOUBLES</option>
                      <option value="WOMENS_DOUBLES">WOMEN'S DOUBLES</option>
                      <option value="SPECTATOR">SPECTATOR PASS</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="notify-email" className="font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] block mb-2">
                    EMAIL
                  </label>
                  <input
                    id="notify-email"
                    type="email"
                    className="field"
                    placeholder="YOU@EMAIL.COM"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="notify-form-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="notify-form-submit"
                  className="btn-solid mt-6 w-full bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 hover:bg-white disabled:opacity-60"
                >
                  {busy ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> JOINING…
                    </span>
                  ) : (
                    "GET FIRST ACCESS"
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
